import api from './api';
import type {
  OrderState as SalesOrderState,
  OrderStatusGroup,
  ShipperDeliveryStatsResponse,
  ShipperProfileStatsResponse,
  ShippingStateCountsResponse,
} from '@tcr-erp/shared';
import type { ShippingListQuery, GhtkLogsQuery } from './queryKeys';

/** Nhãn vận đơn BE trả về: JSON + base64, xem `downloadLabel`. */
export interface ShippingLabelPayload {
  filename: string;
  content_type: string;
  data_base64: string;
}

export interface ShippingLabelFile {
  blob: Blob;
  filename: string;
  content_type: string;
}

/**
 * base64 → Blob. Đi qua Uint8Array chứ không nhét thẳng chuỗi vào Blob: chuỗi
 * base64 mà coi là text thì file PDF hỏng.
 */
export function base64ToBlob(base64: string, contentType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes.buffer], { type: contentType || 'application/pdf' });
}

/**
 * Bám theo mã hãng (xem `ShippingState` ở `@tcr-erp/shared`) — khai lại thành
 * union chuỗi tại đây (không import trực tiếp enum) để giữ tương thích với
 * literal string BE trả về / fixture test hiện có, tránh ràng buộc nominal
 * type của TS enum.
 */
export type ShippingState =
  | 'IDLE'
  | 'AWAITING_PICKUP'
  | 'PICKUP_FAILED'
  | 'PICKED_UP'
  | 'IN_TRANSIT'
  | 'OUT_FOR_DELIVERY'
  | 'DELIVERY_FAILED'
  | 'DELIVERED'
  | 'RETURNING'
  | 'RETURNED'
  | 'LOST'
  | 'DAMAGED'
  | 'CANCELLED'
  // Dữ liệu cũ — chỉ còn ở bản ghi cũ, xem @deprecated ở shared enum.
  | 'IN_PROCESS'
  | 'COMPLETED'
  | 'ASSIGNING'
  | 'ACCEPTED';
export type ShippingPartner = 'AHAMOVE' | 'BEST' | 'GHTK' | 'MANUAL' | 'VIETTELPOST';

export interface ShippingReceiver {
  name: string;
  phone: string;
  address: string;
  city_id?: { _id: string; name: string } | string;
  district_id?: { _id: string; name: string } | string;
  ward_id?: { _id: string; name: string } | string;
}

export interface ShippingPackage {
  /** Kilogram. */
  weight?: number;
  /** Centimetre. */
  length?: number;
  width?: number;
  height?: number;
}

export interface ShippingPickupSnapshot {
  pickup_location_id?: string;
  name?: string;
  address?: string;
  contact_name?: string;
  phone?: string;
}

export interface ShippingLinkedOrder {
  _id: string;
  order_number?: string;
  fulfillment_progress?: string;
  state?: SalesOrderState;
  status_group?: OrderStatusGroup | null;
  created_at?: string;
  confirmed_at?: string;
  completed_at?: string;
  delivery_days?: number;
  delivery_date?: string;
  shipping_state?: string;
  state_history?: Array<{ state: string; changed_at: string }>;
  pos_fulfillment?: { mode?: string };
  shipping?: {
    package?: ShippingPackage;
    pickup_location?: ShippingPickupSnapshot;
    estimated_days?: number;
    estimated_delivery?: { from: string; to: string };
    state?: string;
  };
}

export interface ShippingOrderPackage {
  package_id: string;
  source_order_id: string;
  source_wave_id: string;
  package_code: string;
  sequence: number;
  state: 'AWAITING_PICKUP' | 'PICKED_UP';
  pickup?: {
    scanned_at?: string;
    scanned_by?: string;
    scan_session_id?: string;
  };
}

export interface ShippingPackageSummary {
  total: number;
  picked_up: number;
  awaiting_pickup: number;
  is_complete: boolean;
}

export interface ShippingPartnerInfo {
  name: ShippingPartner;
  order_id?: string;
  route_code?: string;
  /**
   * AI THỰC SỰ CHỞ kiện hàng khi `name = MANUAL` — đội giao của cửa hàng, hoặc
   * dịch vụ thuê ngoài tại chỗ (Grab, Ahamove...).
   *
   * `MANUAL` chỉ nói cửa hàng TỰ LO việc giao, không nói lo bằng cách nào. Đơn
   * MANUAL nào cũng có `name = 'MANUAL'`, nên thiếu field này thì mọi cách giao
   * dồn chung một nhóm "Tự giao hàng" và không phân biệt được nữa.
   */
  carrier_name?: string;
}

/**
 * NGƯỜI giao hàng của vận đơn tự giao (TCR-1518) — khác `carrier_name` là ĐƠN VỊ nào
 * chở. `name`/`phone` là bản CHỤP lúc gán, kể cả khi đã chọn tài khoản ERP.
 */
/**
 * Phản hồi của khách về chuyến giao (TCR-1519). `stars` là thứ duy nhất bắt buộc;
 * mọi chi tiết khác (móp méo, thái độ, giao trễ) nằm trong `feedback`.
 */
export interface ShippingRating {
  stars: number;
  feedback?: string;
  rated_at?: string;
}

export interface ShippingDeliveryman {
  staff_id?: string | { _id?: string } | null;
  name?: string;
  phone?: string;
  assigned_at?: string;
}

/**
 * Tiến trình lấy mã vận đơn của hãng — TÁCH khỏi `state` (vòng đời giao hàng).
 *
 * Một đơn `IDLE` có thể là chưa gửi hãng (`pending`), đã gửi nhưng hãng hết giờ
 * nên không rõ (`uncertain`), hay đang chờ nhân viên nhập tay
 * (`manual_required`). `state` một mình không nói được điều đó.
 */
export type ShippingSubmissionState =
  | 'pending'
  | 'uncertain'
  | 'confirmed'
  | 'manual_required'
  | 'failed';

/** Một mốc trong "Lịch sử cập nhật đơn hàng". */
export interface ShippingTrackingEvent {
  event_key: string;
  source: 'webhook' | 'query' | 'manual';
  carrier_status?: string | null;
  description?: string | null;
  /** Thời điểm sự việc xảy ra theo hãng. */
  occurred_at?: string | null;
  /** Thời điểm TCR-ERP ghi nhận. */
  recorded_at?: string | null;
  /** Vì sao mốc này không đổi được trạng thái đơn, nếu có. */
  note?: string | null;
  facts?: Record<string, number> | null;
}

export interface ShippingRefreshResult {
  order_number: string;
  /** `false` khi hãng chưa hỗ trợ tra cứu — giao diện chỉ đọc lại dữ liệu nội bộ. */
  refreshed: boolean;
  new_events: number;
  reconciled: boolean;
  message: string;
}

export interface ShippingRetryResult {
  order_number: string;
  retried: boolean;
  /** Trạng thái SAU khi gửi lại — đọc lại từ DB, không suy từ lời gọi. */
  submission_state: ShippingSubmissionState;
  tracking_code?: string;
  message: string;
}

/** Loại sự cố bàn giao (TCR-1521) — khớp `ShippingIncidentType` bên backend. */
export type ShippingIncidentType =
  | 'customer_unreachable'
  | 'customer_refused'
  | 'quantity_mismatch'
  | 'goods_damaged'
  | 'address_not_found';

export const SHIPPING_INCIDENT_LABELS: Record<ShippingIncidentType, string> = {
  customer_unreachable: 'Khách không nghe máy / vắng mặt',
  customer_refused: 'Khách từ chối nhận hàng',
  quantity_mismatch: 'Thiếu / thừa số lượng hàng',
  goods_damaged: 'Hàng móp, vỡ, hư hỏng',
  address_not_found: 'Sai địa chỉ / không tìm thấy',
};

/**
 * Bằng chứng giao hàng do app người giao chụp lúc chốt giao (TCR-1795).
 *
 * Dữ liệu gốc nằm trên ĐƠN HÀNG (`pos_fulfillment`), không phải vận đơn; endpoint
 * chi tiết vận đơn tự đi lấy rồi trả kèm.
 */
export interface ShippingDeliveryProof {
  order_id: string;
  order_number?: string;
  delivered_at?: string | null;
  photo_urls: string[];
  signature_url?: string | null;
  proof_id?: string | null;
}

export interface ShippingIncident {
  _id?: string;
  type: ShippingIncidentType;
  /** ÂM = thiếu, DƯƠNG = thừa. Chỉ có nghĩa với `quantity_mismatch`. */
  quantity_delta?: number | null;
  photo_urls?: string[];
  note?: string;
  captured_lat?: number | null;
  captured_lng?: number | null;
  reported_by?: string | null;
  reported_at?: string;
}

export interface ReportShippingIncidentInput {
  type: ShippingIncidentType;
  quantity_delta?: number;
  photo_urls?: string[];
  note?: string;
  captured_lat?: number;
  captured_lng?: number;
}

export interface ShippingOrder {
  _id: string;
  /** Mã NỘI BỘ của hệ thống (`GV-...`) — KHÁC mã vận đơn hãng cấp. */
  order_number: string;
  state: ShippingState;
  submission_state?: ShippingSubmissionState;
  shipping_partner?: ShippingPartnerInfo;
  /** Người giao hàng của vận đơn tự giao (TCR-1518). */
  deliveryman?: ShippingDeliveryman | null;
  /** Đánh giá shipper + phản hồi của khách (TCR-1519). */
  rating?: ShippingRating | null;
  /** Sự cố ghi nhận tại điểm giao (TCR-1521). Chỉ THÊM, không sửa/xoá. */
  incidents?: ShippingIncident[];
  /** Ảnh + chữ ký lúc chốt giao, gom từ các đơn hàng của vận đơn (TCR-1795). */
  delivery_proofs?: ShippingDeliveryProof[];
  receiver: ShippingReceiver;
  pickup_location_id?: PickupLocation | string;
  /**
   * Danh sách list trả về id thô; endpoint chi tiết populate thêm `order_number`
   * để hiển thị mã đơn thay vì _id.
   */
  order_ids: Array<string | ShippingLinkedOrder>;
  /** Snapshot kiện từ Wave; đây là dữ liệu giao vận dùng sau khi đóng gói. */
  package_mode?: 'multi_package';
  package_snapshot_status?: 'pending' | 'ready' | 'failed';
  packages?: ShippingOrderPackage[];
  package_summary?: ShippingPackageSummary;
  packages_snapshotted_at?: string;
  note?: string;
  /**
   * Dữ liệu riêng của hãng. `best_status` là MÃ GỐC mới nhất Best báo về — thứ
   * quyết định đơn còn huỷ được hay không (Best chỉ cho huỷ trước mã 202).
   */
  data?: Record<string, unknown>;
  /**
   * Tiền shipper thu hộ, CHỐT lúc tạo vận đơn. `0` = không thu hộ.
   * Vận đơn tạo trước thay đổi này không có trường này.
   */
  cod_amount?: number;
  /**
   * Lịch sử cập nhật, cũ → mới. Phần tử đầu LUÔN là "Đơn hàng được tạo" (BE
   * dựng từ `created_at`, không lưu trong DB). Chỉ có ở endpoint chi tiết.
   */
  tracking_events?: ShippingTrackingEvent[];
  total_price?: number;
  /** Phí nghiệp vụ của TCR-ERP (người dùng nhập) — KHÁC `fee_estimate`. */
  fee?: number;
  /**
   * Phí THẬT hãng báo qua webhook — khác `fee_estimate` (báo giá lúc tạo) và
   * khác `fee` (số nghiệp vụ người dùng nhập).
   */
  fee_actual?: {
    total?: number;
    freight_fee?: number;
    /** Cước ĐÃ GỒM VAT — Best xác nhận `vat_fee` luôn là bản đã gồm. */
    vat_fee?: number;
    insurance_fee?: number;
    cod_fee?: number;
    /** Giá vốn đã gồm VAT hay chưa (`vat_fee === freight_fee`). */
    vat_included?: boolean;
    provider?: string;
    reported_at?: string;
  };
  /** Phí hãng báo lúc tạo vận đơn. `total` đã gồm VAT. */
  fee_estimate?: {
    total?: number;
    freight_fee?: number;
    cod_fee?: number;
    vat_fee?: number;
    provider?: string;
    estimated_at?: string;
  };
  created_at: string;
  tenant_id?: string;
}

export interface ShippingReconcileResult {
  order_number: string;
  /** `true` khi hãng vừa xác nhận và mã vận đơn đã được ghi. */
  reconciled: boolean;
  /** `not_applicable` = không có gì để đối chiếu (đã có mã, hoặc không phải Best). */
  status: 'not_found' | 'pending' | 'confirmed' | 'not_applicable';
  tracking_code?: string;
  message: string;
}

export interface PickupLocation {
  _id: string;
  name?: string;
  contact_name: string;
  phone: string;
  store_id?: { _id: string; name: string; address?: string; city_id?: string; ward_id?: string } | string;
  state?: number;
  is_default?: boolean;
  /**
   * Địa chỉ lấy hàng gửi cho hãng vận chuyển. Thiếu `address`/`city_id`/`ward_id`
   * thì BE chặn trước khi gọi hãng — xem `getShippingOrderBlockReason`.
   * `district_id` chỉ dùng cho hãng còn theo mô hình 3 cấp (VN đã còn 2 cấp từ
   * 01/07/2025) nên thường là null.
   */
  address?: string;
  city_id?: string | { _id: string; name: string };
  ward_id?: string | { _id: string; name: string };
  district_id?: string | { _id: string; name: string } | null;
}

export interface GhtkLog {
  _id: string;
  order_id?: string;
  order_number: string;
  state?: string | number;
  data?: Record<string, unknown>;
  created_at: string;
}

export interface ShippingListResponse {
  data: ShippingOrder[];
  total: number;
  page: number;
  limit: number;
}

export type PickupScanStatus = 'pending' | 'not_pending' | 'not_found' | 'ambiguous';

export interface PickupScanMatch {
  _id: string;
  order_number: string;
  shipping_partner?: { order_id?: string };
  receiver?: { name?: string };
}

export interface PickupScanResponse {
  status: PickupScanStatus;
  shipping_order: ShippingOrder | null;
  matches?: PickupScanMatch[];
  message: string;
}

export interface GhtkLogsResponse {
  data: GhtkLog[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateShippingPayload {
  order_ids: string[];
  shipping_partner: ShippingPartner;
  pickup_location_id?: string;
  pickup_district_id?: string;
  receiver_name: string;
  receiver_phone: string;
  receiver_address: string;
  receiver_city_id: string;
  receiver_district_id?: string;
  receiver_ward_id?: string;
  note?: string;
  total_price?: number;
  fee?: number;
  insurance_fee?: number;
}

export interface CreatePickupLocationPayload {
  name: string;
  phone: string;
  contact_name?: string;
  store_id?: string;
  is_default?: boolean;
  /** Bắt buộc — hãng vận chuyển cần đủ địa chỉ chi tiết + tỉnh/thành + phường/xã. */
  address: string;
  city_id: string;
  ward_id: string;
  /** Chỉ cho hãng còn theo mô hình 3 cấp; địa chỉ VN đã còn 2 cấp từ 01/07/2025. */
  district_id?: string;
}

/**
 * Sửa nơi lấy hàng — mọi field optional để bổ sung địa chỉ cho bản ghi cũ mà
 * không phải nhập lại toàn bộ. Field không gửi thì BE giữ nguyên.
 */
export type UpdatePickupLocationPayload = Partial<CreatePickupLocationPayload>;

export interface EstimateFeePayload {
  shipping_partner?: ShippingPartner;
  pickup_location_id?: string;
  receiver_address: string;
  receiver_city_id: string;
  receiver_district_id?: string;
  receiver_ward_id?: string;
  weight?: number; // gram, default 100 cho trang sức
  length?: number; // millimetre for Best
  width?: number; // millimetre for Best
  height?: number; // millimetre for Best
  cod?: number;
  value: number;   // tổng giá trị đơn hàng (VND)
}

export type EstimatableCarrierCode = 'ghtk' | 'best';

export interface EstimateFeesPayload {
  providers: EstimatableCarrierCode[];
  store_id?: string;
  /** Nơi lấy hàng cụ thể — BE tính phí từ đúng địa chỉ này thay vì đoán từ `store_id`. */
  pickup_location_id?: string;
  receiver_address: string;
  receiver_city_id: string;
  receiver_ward_id?: string;
  /** Gram. */
  weight: number;
  /** Milimet. */
  length?: number;
  width?: number;
  height?: number;
  /** Khai giá; 0 = không bảo hiểm. */
  value: number;
  cod: number;
}

export interface CarrierFeeEstimate {
  provider: EstimatableCarrierCode;
  status: 'ok' | 'error' | 'timeout';
  fee?: number;
  breakdown?: { freight_fee?: number; cod_fee?: number; vat_fee?: number };
  is_default_price?: boolean;
  message?: string;
}

export interface EstimateFeesResult {
  estimates: CarrierFeeEstimate[];
}

export interface CreateFromOrderResult {
  shipping_order_id: string;
  shipping_order_number: string;
  provider: string;
  called_carrier: boolean;
  tracking_number: string | null;
  state: string;
  submission_state: string;
}

export interface GhtkExtFee {
  display: string;
  title: string;
  amount: number;
  type: string;
  tag_id: number;
  current_ticket_fee: number;
}

export interface EstimateFeeResult {
  fee: number;
  ship_fee_only?: number;
  insurance_fee?: number;
  estimated_pick_time?: string;
  estimated_deliver_time?: string;
  ext_fees?: GhtkExtFee[];
}

export interface BestFeeEstimateResult {
  provider: 'BEST';
  result: unknown;
  message: string | null;
  data: unknown;
}

export interface QueryShippingPayload {
  codes?: string[];
  shipping_order_ids?: string[];
  lang_type?: 'en-US' | 'zh-CN';
}

export interface BestOrderQueryResult {
  provider: 'BEST';
  codes: string[];
  result: unknown;
  message: string | null;
  data: unknown;
}

export interface OrderSummary {
  original_price: number;
  original_price_with_vat: number;
  direct_discount: number;
  voucher_discount: number;
  final_price: number;
  shipping_cost: number;
  vat: number;
  total: number;
  total_items: number;
}

export interface AvailableOrder {
  _id: string;
  order_number: string;
  state: string;
  shipping_state?: string;
  summary?: OrderSummary;
  customer?: { name?: string; phone?: string };
  store_id?: string;
  shipping_note?: string;
  shipping_order?: {
    _id: string;
    order_number: string;
    state: ShippingState;
    shipping_partner?: ShippingPartnerInfo;
    created_at?: string;
    retryable?: boolean;
  };
  shipping?: {
    address?: {
      name?: string;
      phone?: string;
      address?: string;
      city_id?: string;
      district_id?: string;
      ward_id?: string;
      note?: string;
    };
    shipping_partner?: { name?: ShippingPartner };
    shipping_mode?: 'self' | 'carrier';
    shipping_cost?: number;
    cod_amount?: number;
    fee_payer?: 'seller' | 'buyer';
    package?: {
      weight?: number;
      length?: number;
      width?: number;
      height?: number;
    };
  };
  created_at: string;
}

export interface AvailableOrdersResponse {
  data: AvailableOrder[];
  total: number;
  page: number;
  limit: number;
}

export interface ShippingCarrierAvailability {
  provider: string;
  label: string;
  enabled: boolean;
}

/** Một lần gọi API sang đơn vị vận chuyển. Secret luôn đã bị che ở BE. */
export interface ShippingApiLogEntry {
  _id: string;
  carrier: string;
  operation: string;
  method?: string | null;
  url?: string | null;
  environment?: string | null;
  request?: unknown;
  response?: unknown;
  response_status?: number | null;
  outcome:
    | 'ok'
    | 'rejected'
    | 'network_error'
    | 'upstream_5xx'
    | 'duplicate'
    | 'unauthorized'
    | 'invalid_request'
    /** Đã nhận hợp lệ nhưng không áp được vào vận đơn (`Webhook/Apply`). */
    | 'not_applied';
  /** `api` = ta gọi hãng; `webhook` = hãng gọi ta. */
  source?: 'api' | 'webhook';
  duration_ms?: number | null;
  error?: string | null;
  order_number?: string | null;
  shipping_order: {
    _id: string;
    order_number: string;
    carrier_order_number: string | null;
  } | null;
  sales_orders: Array<{
    _id: string;
    order_number: string;
  }>;
  created_at: string;
}

export interface ShippingApiLogQuery {
  page?: number;
  limit?: number;
  carrier?: string;
  source?: string;
  outcome?: string;
  search?: string;
}

export interface ShippingApiLogsResponse {
  data: ShippingApiLogEntry[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Cụm phí kèm theo mốc giả lập. Tên trường giữ nguyên như Best gửi.
 *
 * `total_fee` là công tắc: thiếu nó thì backend bỏ qua cả cụm và không ghi
 * `fee_actual` — nên form phải bắt buộc trường này khi người dùng nhập phí lẻ.
 */
export interface SimulatedBestFees {
  total_fee?: number;
  freight_fee?: number;
  vat_fee?: number;
  insurance_fee?: number;
  cod_fee?: number;
}

export const shippingService = {
  listCarriers: (): Promise<ShippingCarrierAvailability[]> =>
    api.get<{ carriers: ShippingCarrierAvailability[] }>('/shipping/carriers')
      .then((response) => response.data.carriers ?? []),

  list: (params: ShippingListQuery): Promise<ShippingListResponse> =>
    api.get('/shipping', { params }).then((r) => r.data),

  claim: (id: string): Promise<ShippingOrder> =>
    api.post(`/shipping/${id}/claim`).then((r) => r.data),

  getMyStateCounts: (month?: string): Promise<ShippingStateCountsResponse> =>
    api.get('/shipping/my-state-counts', { params: month ? { month } : undefined }).then((r) => r.data),

  scanMyPickup: (code: string): Promise<PickupScanResponse> =>
    api.get('/shipping/my-pickup-scan', { params: { code } }).then((r) => r.data),

  clearPackageScans: (
    shippingOrderId: string,
  ): Promise<{
    cleared_packages: number;
    packages: ShippingOrderPackage[];
    package_summary: ShippingPackageSummary;
    message: string;
  }> => api.post(`/shipping/${shippingOrderId}/packages/clear-scans`).then((r) => r.data),

  scanPackage: (
    shippingOrderId: string,
    packageCode: string,
    scanSessionId?: string,
  ): Promise<{
    status: 'scanned' | 'already_scanned';
    package: ShippingOrderPackage;
    package_summary: ShippingPackageSummary;
    message: string;
  }> => api.post(`/shipping/${shippingOrderId}/packages/scan`, {
    package_code: packageCode,
    ...(scanSessionId ? { scan_session_id: scanSessionId } : {}),
  }).then((r) => r.data),

  confirmPackagePickup: (
    shippingOrderId: string,
    note?: string,
  ): Promise<{
    status: 'confirmed';
    shipping_order_id: string;
    package_summary: ShippingPackageSummary;
    message: string;
  }> => api.post(`/shipping/${shippingOrderId}/packages/confirm-pickup`, {
    ...(note ? { note } : {}),
  }).then((r) => r.data),

  getMyProfileStats: (): Promise<ShipperProfileStatsResponse> =>
    api.get('/shipping/my-profile-stats').then((r) => r.data),

  getMyDeliveryStats: (year: number): Promise<ShipperDeliveryStatsResponse> =>
    api.get('/shipping/my-delivery-stats', { params: { year } }).then((r) => r.data),

  reportIncident: (id: string, body: ReportShippingIncidentInput): Promise<ShippingOrder> =>
    api.post(`/shipping/${id}/incidents`, body).then((r) => r.data),

  startReturn: (id: string): Promise<{ message: string; state: ShippingState }> =>
    api.post(`/shipping/${id}/return`).then((r) => r.data),

  getById: (id: string): Promise<ShippingOrder> =>
    api.get(`/shipping/${id}`).then((r) => r.data),

  create: (body: CreateShippingPayload): Promise<ShippingOrder> =>
    api.post('/shipping', body).then((r) => r.data),

  callGHTK: (id: string, note?: string): Promise<{ message: string; label: string }> =>
    api.post(`/shipping/${id}/call-ghtk`, { note }).then((r) => r.data),

  getTracking: (id: string): Promise<{
    shipping_order: ShippingOrder;
    ghtk_status?: unknown;
    best_status?: BestOrderQueryResult;
  }> =>
    api.get(`/shipping/${id}/tracking`).then((r) => r.data),

  /**
   * Tải file nhãn/vận đơn từ hãng. Chỉ truyền id vận đơn — URL file do hãng cấp
   * và được BE kiểm tra host, FE không bao giờ cầm URL của hãng.
   *
   * BE trả JSON + base64 chứ không phải body nhị phân: extension quản lý download
   * (IDM) bắt response `application/pdf` rồi nẫng luôn, làm XHR chết với
   * ERR_BLOCKED_BY_CLIENT dù server đã trả 200. Xem chú thích ở
   * `shipping.controller.ts`. Dựng lại Blob ngay tại đây để phía trên vẫn chỉ
   * làm việc với Blob như cũ.
   */
  downloadLabel: (id: string): Promise<ShippingLabelFile> =>
    api
      .get<ShippingLabelPayload>(`/shipping/${id}/label`)
      .then((r) => ({
        blob: base64ToBlob(r.data.data_base64, r.data.content_type),
        filename: r.data.filename,
        content_type: r.data.content_type,
      })),

  updateTracking: (
    id: string,
    tracking_number: string,
    carrier_name?: string,
  ): Promise<{ message: string }> =>
    api.patch(`/shipping/${id}/tracking`, { tracking_number, carrier_name }).then((r) => r.data),

  cancel: (id: string, note?: string): Promise<{ message: string }> =>
    api.post(`/shipping/${id}/cancel`, { note }).then((r) => r.data),

  complete: (id: string): Promise<{ message: string }> =>
    api.post(`/shipping/${id}/complete`).then((r) => r.data),

  /**
   * Gán NGƯỜI giao hàng cho vận đơn tự giao (TCR-1518).
   *
   * `staff_id` khi chọn từ tài khoản ERP, `name`/`phone` khi gõ tay — gửi cả ba cũng
   * được: máy chủ lấy cái người dùng gõ đè lên thông tin của tài khoản.
   */
  setDeliveryman: (id: string, body: { staff_id?: string; name?: string; phone?: string }) =>
    api.put(`/shipping/${id}/deliveryman`, body).then((r) => r.data),

  clearDeliveryman: (id: string) =>
    api.delete(`/shipping/${id}/deliveryman`).then((r) => r.data),

  /** Ghi nhận đánh giá shipper cho vận đơn tự giao (TCR-1519). */
  setRating: (id: string, body: { stars: number; feedback?: string }) =>
    api.put(`/shipping/${id}/rating`, body).then((r) => r.data),

  clearRating: (id: string) =>
    api.delete(`/shipping/${id}/rating`).then((r) => r.data),

  /**
   * Giả lập webhook Best — chạy được ở mọi môi trường.
   *
   * Khác hẳn `complete`: đường này đi đúng cửa Best thật gõ vào, nên để lại đủ
   * dấu vết (`data.best_status`, `tracking_events`, `process_logs` nguồn
   * `carrier_webhook`) — thứ mà nút "Hoàn thành" thủ công không hề đụng tới.
   */
  simulateBestWebhook: (
    id: string,
    status?: string,
    fees?: SimulatedBestFees,
  ): Promise<{
    simulated: true;
    status: string;
    merchant_order_code: string;
    /**
     * Đã chốt luôn phần giao hàng BÊN TA hay chưa.
     *
     * CHỈ đúng với mã `666`; mọi mã khác chỉ đi đường webhook. Trường này BE trả
     * về sẵn từ đầu nhưng FE bỏ qua, nên câu thông báo cũ nói "đã chốt giao hàng"
     * cho mọi lần bấm — đúng hồi nút chỉ bắn được `666`, sai từ lúc mở hết bảng mã.
     */
    fulfillment_completed: boolean;
  }> =>
    api
      .post(`/shipping/${id}/simulate-best-webhook`, { status, ...fees })
      .then((r) => r.data),

  listPickupLocations: (): Promise<{ data: PickupLocation[] }> =>
    api.get('/shipping/pickup-locations').then((r) => r.data),

  createPickupLocation: (body: CreatePickupLocationPayload): Promise<PickupLocation> =>
    api.post('/shipping/pickup-locations', body).then((r) => r.data),

  updatePickupLocation: (
    id: string,
    body: UpdatePickupLocationPayload,
  ): Promise<PickupLocation> =>
    api.patch(`/shipping/pickup-locations/${id}`, body).then((r) => r.data),

  getGhtkLogs: (params: GhtkLogsQuery): Promise<GhtkLogsResponse> =>
    api.get('/shipping/ghtk-logs', { params }).then((r) => r.data),

  /** Nhật ký gọi API tới mọi DVVC (TCR shipping logs). */
  getShippingApiLogs: (params: ShippingApiLogQuery): Promise<ShippingApiLogsResponse> =>
    api.get('/shipping/logs', { params }).then((r) => r.data),

  getShippingApiLog: (id: string): Promise<{ data: ShippingApiLogEntry | null }> =>
    api.get(`/shipping/logs/${id}`).then((r) => r.data),

  getMapData: (params: ShippingListQuery): Promise<ShippingListResponse> =>
    api.get('/shipping/map', { params }).then((r) => r.data),

  estimateFee: (body: EstimateFeePayload): Promise<EstimateFeeResult | BestFeeEstimateResult> =>
    api.post('/shipping/estimate-fee', body).then((r) => r.data),

  estimateFees: (body: EstimateFeesPayload): Promise<EstimateFeesResult> =>
    api.post('/shipping/estimate-fees', body).then((r) => r.data),

  /** Nút "Tạo và Đi đơn" ở POS. Idempotent theo order_id — bấm lại an toàn. */
  createShippingFromOrder: (order_id: string): Promise<CreateFromOrderResult> =>
    api.post('/shipping/from-order', { order_id }).then((r) => r.data),

  queryBestOrders: (body: QueryShippingPayload): Promise<BestOrderQueryResult> =>
    api.post('/shipping/query', body).then((r) => r.data),

  getAvailableOrders: (params: { page?: number; limit?: number; q?: string }): Promise<AvailableOrdersResponse> =>
    api.get('/shipping/available-orders', { params }).then((r) => r.data),

  /** Hỏi hãng về ĐÚNG vận đơn này (Best `Order/Query`). Thay cho vòng cron cũ. */
  reconcileShippingOrder: (id: string): Promise<ShippingReconcileResult> =>
    api.post(`/shipping/${id}/reconcile`).then((r) => r.data),

  /** Nút "Làm mới": hỏi thẳng hãng rồi mới đọc lại dữ liệu TCR-ERP. */
  refreshShippingOrder: (id: string): Promise<ShippingRefreshResult> =>
    api.post(`/shipping/${id}/refresh`).then((r) => r.data),

  /**
   * Gửi lại vận đơn bị hãng TỪ CHỐI, dùng lại chính bản ghi và mã đối tác cũ.
   *
   * KHÁC "tạo vận đơn mới": cái đó cấp mã đối tác mới, và với Best thì mã mới
   * nghĩa là mất lá chắn khử trùng — có thể thành hai kiện hàng thật.
   */
  retryShippingSubmission: (id: string): Promise<ShippingRetryResult> =>
    api.post(`/shipping/${id}/retry-submission`).then((r) => r.data),
};
